/**
 * ⚠️ HAND-WRITTEN FILE - DO NOT DELETE! ⚠️
 *
 * macOS-ish Finder chrome over the Cover Flow canvas.
 * Canvas is behind this overlay; only chrome + the flow layer receive pointers.
 */

import type { PointerEvent } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { Link } from '@tanstack/react-router';
import { FINDER_FOLDERS, FINDER_FOLDER_CHROME, hexAlpha, isMusicFinderKind, CONNECT_LABEL_FONT_FAMILY, PRODENG_LABEL_FONT_FAMILY, type FinderFolderId, type FinderItem, type MusicView } from '@/lib/homeFinder';
import { isUsableAlbumArtUrl } from '@/lib/musicSleeve';
import { SOCIAL_LINKS, isSocialLinkId } from '@/lib/socialLinks';
import { analytics } from '@/lib/analytics';
import { HomeSocialIcon } from './HomeSocialIcon';
import { MusicSleeveThumb } from './MusicSleeveThumb';
import { HomeFinderKey } from './HomeFinderKey';

interface HomeFinderWindowProps {
  folder: FinderFolderId;
  onFolder: (folder: FinderFolderId) => void;
  musicView: MusicView;
  onMusicView: (view: MusicView) => void;
  items: FinderItem[];
  selectedIndex: number;
  onSelect: (index: number) => void;
  onOpen: () => void;
  onFlowPointerDown?: (event: PointerEvent<HTMLDivElement>) => void;
  onFlowPointerMove?: (event: PointerEvent<HTMLDivElement>) => void;
  onFlowPointerUp?: (event: PointerEvent<HTMLDivElement>) => void;
  reducedMotion: boolean;
  /** Empty Cover Flow stage: chrome only, no 2D grid (SSR / before WebGL). */
  pending?: boolean;
}

const MUSIC_VIEWS: Array<{ id: MusicView; label: string }> = [
  { id: 'recents', label: 'Recents' },
  { id: 'playlists', label: 'Playlists' },
  { id: 'liked', label: 'Liked' },
];

export function HomeFinderWindow({
  folder,
  onFolder,
  musicView,
  onMusicView,
  items,
  selectedIndex,
  onSelect,
  onOpen,
  onFlowPointerDown,
  onFlowPointerMove,
  onFlowPointerUp,
  reducedMotion,
  pending = false,
}: HomeFinderWindowProps) {
  const selected = items[selectedIndex];
  const folderLabel = FINDER_FOLDERS.find((entry) => entry.id === folder)?.label ?? 'Finder';
  const chrome = FINDER_FOLDER_CHROME[folder];
  const captionFont =
    selected?.kind === 'social' ? CONNECT_LABEL_FONT_FAMILY : folder === 'prodeng' ? PRODENG_LABEL_FONT_FAMILY : undefined;

  return (
    <Box
      sx={{
        position: 'absolute',
        inset: { xs: 8, md: 16 },
        display: 'flex',
        flexDirection: 'column',
        borderRadius: '12px',
        overflow: 'hidden',
        border: '1px solid rgba(255,255,255,0.14)',
        boxShadow: '0 24px 80px rgba(0,0,0,0.45)',
        pointerEvents: 'none',
        color: 'white',
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        spacing={1.5}
        sx={{
          px: 1.5,
          py: 1,
          pointerEvents: 'auto',
          borderBottom: '1px solid rgba(255,255,255,0.08)',
          bgcolor: 'rgba(28, 28, 30, 0.82)',
          backdropFilter: 'blur(18px)',
        }}
      >
        <Stack direction="row" spacing={0.75} aria-hidden>
          {['#ff5f57', '#febc2e', '#28c840'].map((color) => (
            <Box key={color} sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: color, opacity: 0.9 }} />
          ))}
        </Stack>
        <Typography
          variant="body2"
          sx={{
            flex: 1,
            minWidth: 0,
            fontWeight: 600,
            opacity: 0.85,
            letterSpacing: '0.02em',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          Finder — {folderLabel}
        </Typography>
        <Box sx={{ display: { xs: 'none', md: 'flex' } }}>
          <HomeFinderKey showScroll={!reducedMotion} />
        </Box>
        {selected && (
          <Button
            key={folder}
            size="small"
            variant="contained"
            onClick={onOpen}
            sx={{
              flexShrink: 0,
              textTransform: 'none',
              fontWeight: 700,
              boxShadow: 'none',
              transition: 'none',
              bgcolor: chrome.fill,
              color: chrome.ink,
              '&:hover': { bgcolor: chrome.hover, boxShadow: 'none' },
              '&.MuiButton-contained': {
                bgcolor: chrome.fill,
                color: chrome.ink,
              },
            }}
          >
            Open
          </Button>
        )}
      </Stack>

      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          minHeight: 0,
        }}
      >
        <Box
          component="nav"
          aria-label="Site folders"
          sx={{
            pointerEvents: 'auto',
            width: { xs: '100%', md: 168 },
            flexShrink: 0,
            borderRight: { md: '1px solid rgba(255,255,255,0.08)' },
            borderBottom: { xs: '1px solid rgba(255,255,255,0.08)', md: 'none' },
            bgcolor: 'rgba(28, 28, 30, 0.82)',
            backdropFilter: 'blur(18px)',
            px: 1,
            py: 1,
            overflowX: { xs: 'auto', md: 'hidden' },
            display: 'flex',
            flexDirection: { xs: 'row', md: 'column' },
            gap: 0.5,
          }}
        >
          {FINDER_FOLDERS.map((entry) => {
            const active = entry.id === folder;
            const entryChrome = FINDER_FOLDER_CHROME[entry.id];
            return (
              <Button
                key={entry.id}
                onClick={() => onFolder(entry.id)}
                sx={{
                  justifyContent: 'flex-start',
                  textTransform: 'none',
                  color: 'white',
                  fontWeight: active ? 700 : 500,
                  bgcolor: active ? entryChrome.fill : 'transparent',
                  minWidth: 'auto',
                  whiteSpace: 'nowrap',
                  px: 1.25,
                  transition: 'none',
                  '&:hover': {
                    bgcolor: active ? entryChrome.hover : hexAlpha(entryChrome.fill, 0.35),
                  },
                }}
              >
                {entry.label}
              </Button>
            );
          })}
        </Box>

        <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', position: 'relative' }}>
          {folder === 'music' && (
            <Stack
              direction="row"
              spacing={1}
              sx={{
                pointerEvents: 'auto',
                px: 1.5,
                pt: 1,
                pb: 0.5,
                background: 'linear-gradient(rgba(15, 23, 42, 0.55), transparent)',
              }}
              role="tablist"
              aria-label="Music views"
            >
              {MUSIC_VIEWS.map((view) => (
                <Button
                  key={view.id}
                  role="tab"
                  aria-selected={musicView === view.id}
                  onClick={() => onMusicView(view.id)}
                  size="small"
                  sx={{
                    textTransform: 'none',
                    color: 'white',
                    fontWeight: musicView === view.id ? 700 : 500,
                    bgcolor: musicView === view.id ? hexAlpha(chrome.fill, 0.45) : 'rgba(255,255,255,0.08)',
                  }}
                >
                  {view.label}
                </Button>
              ))}
              <Button
                component={Link}
                to="/music"
                size="small"
                sx={{ ml: 'auto', textTransform: 'none', color: 'white', opacity: 0.85 }}
              >
                Open /music
              </Button>
            </Stack>
          )}

          <Box
            sx={{
              flex: 1,
              position: 'relative',
              pointerEvents: 'auto',
              minHeight: { xs: 220, md: 0 },
            }}
            onPointerDown={onFlowPointerDown}
            onPointerMove={onFlowPointerMove}
            onPointerUp={onFlowPointerUp}
            onPointerCancel={onFlowPointerUp}
          >
            {reducedMotion && (
              <Box
                sx={{
                  position: 'absolute',
                  inset: 8,
                  overflow: 'auto',
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))',
                  gap: 1.5,
                  p: 1,
                }}
              >
                {items.map((item, index) => (
                  <Button
                    key={item.id}
                    onClick={() => {
                      if (index === selectedIndex) onOpen();
                      else onSelect(index);
                    }}
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'stretch',
                      textTransform: 'none',
                      color: 'white',
                      p: 0.5,
                      border: index === selectedIndex ? '2px solid white' : '2px solid transparent',
                      bgcolor: item.color ?? 'rgba(255,255,255,0.08)',
                    }}
                  >
                    {item.kind === 'social' && isSocialLinkId(item.id) ? (
                      <Box
                        sx={{
                          aspectRatio: '3 / 4',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: 1,
                          color: 'white',
                        }}
                      >
                        <HomeSocialIcon id={item.id} sx={{ fontSize: 52, color: 'white' }} />
                        <Typography variant="caption" sx={{ fontWeight: 400, px: 0.5, fontFamily: CONNECT_LABEL_FONT_FAMILY }} noWrap>
                          {item.subtitle}
                        </Typography>
                      </Box>
                    ) : isMusicFinderKind(item.kind) && !isUsableAlbumArtUrl(item.previewUrl) ? (
                      <MusicSleeveThumb
                        title={item.title}
                        artist={item.subtitle}
                        nowPlaying={item.nowPlaying}
                      />
                    ) : (
                      item.previewUrl && (
                        <Box
                          component="img"
                          src={item.previewUrl}
                          alt=""
                          sx={{ width: '100%', aspectRatio: '3 / 4', objectFit: 'cover', borderRadius: 1 }}
                        />
                      )
                    )}
                    <Typography
                      variant="caption"
                      sx={{
                        mt: 0.5,
                        fontWeight: 600,
                        fontFamily: item.kind === 'article' ? PRODENG_LABEL_FONT_FAMILY : undefined,
                      }}
                      noWrap
                    >
                      {item.title}
                    </Typography>
                  </Button>
                ))}
              </Box>
            )}
            {pending && (
              <Box
                aria-hidden
                sx={{
                  position: 'absolute',
                  inset: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  pointerEvents: 'none',
                }}
              >
                <Box
                  sx={{
                    width: { xs: 168, md: 220 },
                    aspectRatio: '3 / 4',
                    borderRadius: '10px',
                    bgcolor: 'rgba(255,255,255,0.07)',
                    boxShadow: '0 18px 40px rgba(0,0,0,0.28)',
                  }}
                />
              </Box>
            )}
          </Box>

          <Box
            sx={{
              pointerEvents: 'none',
              display: 'flex',
              justifyContent: 'center',
              px: { xs: 1.25, md: 2 },
              ...(reducedMotion
                ? { py: 1 }
                : {
                    position: 'absolute',
                    left: 0,
                    right: 0,
                    bottom: { xs: 8, md: 12 },
                    zIndex: 1,
                  }),
            }}
          >
            <Box
              sx={{
                maxWidth: '100%',
                px: { xs: 1.5, md: 2 },
                py: { xs: 0.75, md: 1 },
                borderRadius: 2,
                textAlign: 'center',
                bgcolor: 'rgba(15, 23, 42, 0.78)',
                backdropFilter: 'blur(16px)',
                WebkitBackdropFilter: 'blur(16px)',
                border: '1px solid rgba(255,255,255,0.14)',
                boxShadow: '0 10px 32px rgba(0,0,0,0.4)',
              }}
            >
              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: selected?.kind === 'social' ? 400 : 700,
                  fontFamily: captionFont,
                  color: folder === 'prodeng' ? '#fef3c7' : undefined,
                  fontSize: { xs: '0.95rem', md: '1.1rem' },
                  lineHeight: 1.3,
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                }}
              >
                {selected?.title ?? 'Nothing selected'}
              </Typography>
              {selected?.subtitle && (
                <Typography
                  variant="caption"
                  sx={{
                    display: 'block',
                    mt: 0.25,
                    opacity: 0.88,
                    fontFamily: captionFont,
                    color: folder === 'prodeng' ? '#fde68a' : undefined,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {selected.subtitle}
                </Typography>
              )}
            </Box>
          </Box>
        </Box>
      </Box>

      <Stack
        direction="row"
        alignItems="center"
        spacing={0.5}
        sx={{
          pointerEvents: 'auto',
          px: 1,
          py: 0.5,
          borderTop: '1px solid rgba(255,255,255,0.08)',
          bgcolor: 'rgba(28, 28, 30, 0.82)',
          backdropFilter: 'blur(18px)',
          overflowX: 'auto',
        }}
        aria-label="Connect"
      >
        {SOCIAL_LINKS.map((link) => (
          <IconButton
            key={link.id}
            component="a"
            href={link.href}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={link.label}
            onClick={() => analytics.outboundLinkClicked(link.href, 'home-connect')}
            sx={{ color: 'white', '&:hover': { color: link.color } }}
          >
            <HomeSocialIcon id={link.id} />
          </IconButton>
        ))}
      </Stack>
    </Box>
  );
}
